import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FilePlus2, Inbox } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { relativeTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { PageEmptyState } from '@deepguide-ai/ui/page-empty-state'
import { InboxListEmptyState } from '@/components/inbox/InboxListEmptyState'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { ReviewStatusIcon } from '@/components/compliance/ReviewStatusIcon'
import type {
  OpenRequestDTO,
  OpenRequestProposalDTO,
  OpenRequestSeverity,
} from '@deepguide-ai/types'

// Shared list of open auditor requests. Fetches internally from
// `auditor.listOpenRequests` (deduped by TanStack Query across mount points);
// callers pass a scope + an `onOpenRequest` handler that owns navigation.
// No resolve action here — resolution always happens in the task detail.

type OpenRequestScope = {
  /** Scope to one audit cycle (Audit Cycle tab). Omit for workspace-wide. */
  auditCycleId?: string
  /** Scope to a single test (per-test view). */
  testId?: string
  /**
   * When true, resolved threads are included alongside unresolved ones and
   * rendered in a muted "Resolved" group below the active severity groups.
   * Use for overview views where the user wants a full picture; leave off
   * (default) for action-oriented lists like the Inbox tab.
   */
  includeResolved?: boolean
}

// Most urgent first — the customer's severity groups, in render order.
const SEVERITY_SECTIONS: {
  severity: OpenRequestSeverity
  label: string
  dot: string
}[] = [
  { severity: 'action_required', label: 'Action required', dot: 'bg-destructive' },
  { severity: 'attention', label: 'Attention', dot: 'bg-amber-400' },
  { severity: 'info', label: 'Informational', dot: 'bg-primary' },
]

const dotFor = (severity: OpenRequestSeverity) =>
  SEVERITY_SECTIONS.find((s) => s.severity === severity)!.dot

/**
 * Whose queue the list is rendering. `severity` is authored from the
 * CUSTOMER's side — "action required" means the customer owes a reply — so the
 * auditor's own page must not reuse those labels verbatim.
 */
type OpenRequestsPerspective = 'customer' | 'auditor'

type ListSection = {
  key: string
  label: string
  dot: string
  items: OpenRequestDTO[]
}

/**
 * Cuts the open-request list into its rendered groups. Pure and exported so the
 * grouping policy can be tested without mounting the list.
 *
 * The customer sees the server's severity buckets as authored. The auditor sees
 * the queue re-cut by WHO OWES THE NEXT MOVE, because severity answers a
 * different question for them: a request the auditor sent and hasn't heard back
 * on is not the auditor's action, however urgent it is. Note `isAuditor` —
 * roots are not all auditor-authored, and a customer-authored thread is waiting
 * on the auditor, not on the customer.
 */
export function buildOpenRequestSections(
  items: OpenRequestDTO[],
  perspective: OpenRequestsPerspective,
): ListSection[] {
  const awaiting = items.filter((i) => !i.resolvedAt && !i.respondedAt)
  const responded = items.filter((i) => !i.resolvedAt && i.respondedAt)
  const resolved = items.filter((i) => i.resolvedAt)

  const resolvedSection: ListSection = {
    key: 'resolved',
    label: 'Resolved',
    dot: 'bg-icon-muted',
    items: resolved,
  }

  const sections: ListSection[] =
    perspective === 'auditor'
      ? [
          {
            key: 'responded',
            label: 'Action required: your review',
            dot: dotFor('action_required'),
            items: [
              ...responded,
              ...awaiting.filter((i) => !i.isAuditor && i.severity !== 'info'),
            ],
          },
          {
            key: 'waiting',
            label: 'Waiting on customer',
            dot: dotFor('attention'),
            items: awaiting.filter(
              (i) => i.isAuditor && i.severity !== 'info',
            ),
          },
          {
            key: 'info',
            label: 'Informational',
            dot: dotFor('info'),
            items: awaiting.filter((i) => i.severity === 'info'),
          },
          resolvedSection,
        ]
      : [
          ...SEVERITY_SECTIONS.map(({ severity, label, dot }) => ({
            key: severity,
            label,
            dot,
            items: awaiting.filter((i) => i.severity === severity),
          })),
          {
            key: 'responded',
            label: 'Responded, needs review',
            dot: 'bg-primary',
            items: responded,
          },
          resolvedSection,
        ]

  return sections.filter((section) => section.items.length > 0)
}

function OpenRequestRow({
  item,
  onClick,
  isSelected,
}: {
  item: OpenRequestDTO
  onClick: () => void
  isSelected?: boolean
}) {
  const testCode = item.testCode ?? item.targetRef
  const isResolved = Boolean(item.resolvedAt)
  const isResponded = Boolean(item.respondedAt) && !isResolved
  return (
    <div
      data-inbox-item-id={item.id}
      className={cn(
        'group flex cursor-pointer gap-3 border-b border-border px-3 py-2.5 transition-colors',
        isSelected ? 'bg-selected' : 'hover:bg-surface-subtle',
        isResolved && !isSelected && 'opacity-60',
      )}
      onClick={onClick}
    >
      <div className='shrink-0 pt-0.5'>
        <ReviewStatusIcon
          reviewStatus={item.reviewStatus}
          commentType={item.commentType}
        />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-[13px] leading-tight text-text-secondary'>
          {testCode && (
            <span className='font-mono text-[11px] text-text-placeholder'>
              {testCode}{' '}
            </span>
          )}
          <span className={cn('font-medium', isResolved && 'line-through')}>
            {item.verb}
          </span>
          {isResolved && (
            <span className='ml-2 text-[10px] font-medium uppercase tracking-[1px] text-text-subtle'>
              Resolved
            </span>
          )}
          {isResponded && (
            <span className='ml-2 text-[10px] font-medium uppercase tracking-[1px] text-cobalt-strong'>
              Responded
            </span>
          )}
        </p>
        <p className='mt-0.5 truncate text-[12px] leading-tight text-text-subtle'>
          {item.content}
        </p>
        <div className='mt-1 flex items-center gap-1.5 text-[11px] text-text-placeholder'>
          <MemberAvatar person={{ name: item.authorName }} size='xs' />
          <span className='truncate'>{item.authorName ?? 'Auditor'}</span>
          <span className='shrink-0'>· {relativeTime(item.createdAt)}</span>
          {item.replyCount > 0 && (
            <span className='shrink-0'>
              · {item.replyCount} {item.replyCount === 1 ? 'reply' : 'replies'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * RFC-226 — a pending proposal row. Deliberately NOT an `OpenRequestRow`: it
 * carries no severity, no reply count and no resolve lifecycle; what it wants
 * is a decision, which the detail panel provides.
 */
function ProposalRow({
  item,
  onClick,
  isSelected,
}: {
  item: OpenRequestProposalDTO
  onClick: () => void
  isSelected?: boolean
}) {
  const isModify = item.kind === 'modify'
  return (
    <div
      data-inbox-item-id={item.id}
      className={cn(
        'group flex cursor-pointer gap-3 border-b border-border px-3 py-2.5 transition-colors',
        isSelected ? 'bg-selected' : 'hover:bg-surface-subtle',
      )}
      onClick={onClick}
    >
      <div className='shrink-0 pt-0.5'>
        <FilePlus2 className='h-4 w-4 text-cobalt-strong' />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-[13px] leading-tight text-text-secondary'>
          {isModify && item.targetTaskCode && (
            <span className='font-mono text-[11px] text-text-placeholder'>
              TEST-{item.targetTaskCode}{' '}
            </span>
          )}
          <span className='font-medium'>
            {isModify ? 'Test change proposed' : 'New test proposed'}
          </span>
        </p>
        <p className='mt-0.5 truncate text-[12px] leading-tight text-text-subtle'>
          {item.title}
        </p>
        <div className='mt-1 flex items-center gap-1.5 text-[11px] text-text-placeholder'>
          <MemberAvatar person={{ name: item.proposedByName }} size='xs' />
          <span className='truncate'>{item.proposedByName ?? 'Auditor'}</span>
          {item.proposedAt && (
            <span className='shrink-0'>· {relativeTime(item.proposedAt)}</span>
          )}
          <span className='shrink-0 truncate font-mono'>· {item.auditorRef}</span>
          {item.commentCount > 0 && (
            <span className='shrink-0'>
              · {item.commentCount}{' '}
              {item.commentCount === 1 ? 'comment' : 'comments'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/** Loading rows for this list. Exported so the auditor Inbox's route-level
 *  skeleton shows the same rows the list itself will render. */
export function OpenRequestsSkeleton({ rows = 6 }: { rows?: number } = {}) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className='flex gap-3 border-b border-border px-3 py-2.5'
        >
          <Skeleton className='h-5 w-5 shrink-0 rounded-full' />
          <div className='flex-1 space-y-1.5'>
            <Skeleton className='h-3.5 w-1/3' />
            <Skeleton className='h-3 w-2/3' />
            <Skeleton className='h-3 w-1/2' />
          </div>
        </div>
      ))}
    </div>
  )
}

export function OpenRequestsList({
  scope = {},
  onOpenRequest,
  onOpenProposal,
  selectedId,
  onVisibleItemsChange,
  className,
  emptyMessage = "No open auditor requests. You're all caught up.",
  emptyVariant = 'card',
  perspective = 'customer',
}: {
  scope?: OpenRequestScope
  onOpenRequest: (item: OpenRequestDTO) => void
  /**
   * RFC-226 — handler for a pending proposal row. Omit it and the Proposals
   * category doesn't render: a surface that can't act on a proposal shouldn't
   * list one (the auditor's own page already has the Request List for that).
   */
  onOpenProposal?: (item: OpenRequestProposalDTO) => void
  /** Open request id (comment id, or `requestId:key` for a proposal) to
   *  highlight as the active selection. */
  selectedId?: string
  onVisibleItemsChange?: (items: OpenRequestDTO[]) => void
  className?: string
  emptyMessage?: string
  /** `inbox` matches Inbox list panel empty layout; `card` uses dashed PageEmptyState. */
  emptyVariant?: 'inbox' | 'card'
  /** Whose queue this is. Defaults to the customer's. */
  perspective?: OpenRequestsPerspective
}) {
  const query = useQuery(trpc.auditor.listOpenRequests.queryOptions(scope))

  const proposals = useMemo(
    () => (onOpenProposal ? (query.data?.proposals ?? []) : []),
    [query.data, onOpenProposal],
  )

  const sections = useMemo(
    () => buildOpenRequestSections(query.data?.items ?? [], perspective),
    [query.data, perspective],
  )

  const visibleItems = useMemo(
    () => sections.flatMap((section) => section.items),
    [sections],
  )

  useEffect(() => {
    onVisibleItemsChange?.(visibleItems)
  }, [onVisibleItemsChange, visibleItems])

  if (query.isLoading) {
    return (
      <div className={className}>
        <OpenRequestsSkeleton />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center py-16 text-center',
          className,
        )}
      >
        <p className='text-[13px] text-text-subtle'>
          Couldn't load open requests.
        </p>
      </div>
    )
  }

  if (sections.length === 0 && proposals.length === 0) {
    if (emptyVariant === 'inbox') {
      return (
        <InboxListEmptyState
          variant='requests'
          description={emptyMessage}
          className={className}
        />
      )
    }
    return (
      <PageEmptyState
        icon={Inbox}
        title='All caught up'
        description={emptyMessage}
        className={cn('min-h-[320px]', className)}
      />
    )
  }

  return (
    <ScrollArea
      className={cn('min-h-0', className)}
      type='scroll'
      viewClassName='[&>div]:!block'
    >
      {proposals.length > 0 && (
        <div>
          <div className='flex items-center gap-2 border-b border-border bg-surface-subtle px-3 py-1.5'>
            <span className='h-1.5 w-1.5 shrink-0 rounded-full bg-cobalt-solid' />
            <span className='text-[10px] font-semibold uppercase tracking-[1.2px] text-text-subtle'>
              Proposals awaiting your decision
            </span>
            <span className='text-[10px] tabular-nums text-text-placeholder'>
              {proposals.length}
            </span>
          </div>
          {proposals.map((item) => (
            <ProposalRow
              key={item.id}
              item={item}
              isSelected={item.id === selectedId}
              onClick={() => onOpenProposal?.(item)}
            />
          ))}
        </div>
      )}
      {sections.map((section) => (
        <div key={section.key}>
          <div className='flex items-center gap-2 border-b border-border bg-surface-subtle px-3 py-1.5'>
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', section.dot)}
            />
            <span className='text-[10px] font-semibold uppercase tracking-[1.2px] text-text-subtle'>
              {section.label}
            </span>
            <span className='text-[10px] tabular-nums text-text-placeholder'>
              {section.items.length}
            </span>
          </div>
          {section.items.map((item) => (
            <OpenRequestRow
              key={item.id}
              item={item}
              isSelected={item.id === selectedId}
              onClick={() => onOpenRequest(item)}
            />
          ))}
        </div>
      ))}
    </ScrollArea>
  )
}
